import React, { useEffect, useState } from "react";
import { getDatabase, ref as dbRef, push } from "firebase/database";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import Navbar from "../../components/navbar/Navbar";
import Footer from "../footer/Footer";
import { storage } from "../../firebaseConfig";
import logo from "../../assets/LogoSalesSprc.png";
import "./adminpanel.css";

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [login, setLogin] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  const [blog, setBlog] = useState({ title: "", date: "", text: "", images: [] });
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState("");
  const [uploadError, setUploadError] = useState("");

  /* Object URLs are revoked when the previews change so long editing sessions
     do not leak blobs. */
  useEffect(
    () => () => imagePreviews.forEach((url) => URL.revokeObjectURL(url)),
    [imagePreviews]
  );

  const handleLoginChange = (event) => {
    setLogin({ ...login, [event.target.name]: event.target.value });
    setLoginError("");
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setLoginLoading(true);
    try {
      await signInWithEmailAndPassword(
        getAuth(),
        login.username,
        login.password
      );
      setIsAuthenticated(true);
      setLoginError("");
    } catch {
      setLoginError("That email and password did not match an admin account.");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleBlogChange = (event) =>
    setBlog({ ...blog, [event.target.name]: event.target.value });

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    setBlog({ ...blog, images: files });
    setImagePreviews(files.map((file) => URL.createObjectURL(file)));
  };

  const handleBlogSubmit = async (event) => {
    event.preventDefault();
    setUploading(true);
    setUploadSuccess("");
    setUploadError("");

    try {
      const imageUrls = [];
      for (const file of blog.images) {
        const storageRef = ref(
          storage,
          `blog_images/${blog.date}_${Date.now()}_${file.name}`
        );
        await uploadBytesResumable(storageRef, file);
        imageUrls.push(await getDownloadURL(storageRef));
      }

      await push(dbRef(getDatabase(), "blogs"), {
        title: blog.title,
        date: blog.date,
        text: blog.text,
        images: imageUrls,
        createdAt: new Date().toISOString(),
      });

      setUploadSuccess("Story published. It is live on the site now.");
      setBlog({ title: "", date: "", text: "", images: [] });
      setImagePreviews([]);
    } catch {
      setUploadError("The story did not upload. Check your connection and try again.");
    } finally {
      setUploading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <main className="admin-gate" id="main">
        <form className="admin-gate__card" onSubmit={handleLoginSubmit}>
          <img src={logo} alt="" width={52} height={52} aria-hidden="true" />
          <h1 className="t-h3 admin-gate__title">Admin sign in</h1>
          <p className="t-sm admin-gate__sub">
            For SALES staff publishing stories to the site.
          </p>

          <div className="field">
            <label className="field__label" htmlFor="admin-email">
              Email
            </label>
            <input
              id="admin-email"
              type="email"
              name="username"
              className="input-sales"
              autoComplete="username"
              value={login.username}
              onChange={handleLoginChange}
              required
              autoFocus
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="admin-password">
              Password
            </label>
            <input
              id="admin-password"
              type="password"
              name="password"
              className="input-sales"
              autoComplete="current-password"
              value={login.password}
              onChange={handleLoginChange}
              required
            />
          </div>

          <div aria-live="polite">
            {loginError ? (
              <p className="notice notice--error">{loginError}</p>
            ) : null}
          </div>

          <button
            type="submit"
            className="btn-sales btn-sales--block"
            disabled={loginLoading}
          >
            {loginLoading ? (
              <>
                <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                Signing in…
              </>
            ) : (
              "Sign in"
            )}
          </button>

          <a className="link-arrow admin-gate__back" href="/">
            Back to the site
          </a>
        </form>
      </main>
    );
  }

  return (
    <div className="admin-page">
      <Navbar />
      <main className="admin-main" id="main">
        <div className="shell admin-shell">
          <p className="marker">
            <span className="marker__num">00</span>
            <span>Admin</span>
          </p>
          <h1 className="t-h2 admin-title">Publish a story</h1>
          <p className="t-lead admin-sub">
            Stories appear on the home page and get their own page at
            /blogpost.
          </p>

          <form className="admin-form card-sales" onSubmit={handleBlogSubmit}>
            <div className="field">
              <label className="field__label" htmlFor="blog-title">
                Title <span className="field__req">*</span>
              </label>
              <input
                id="blog-title"
                type="text"
                className="input-sales"
                name="title"
                value={blog.title}
                onChange={handleBlogChange}
                required
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="blog-date">
                Date <span className="field__req">*</span>
              </label>
              <input
                id="blog-date"
                type="date"
                className="input-sales"
                name="date"
                value={blog.date}
                onChange={handleBlogChange}
                required
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="blog-text">
                Story <span className="field__req">*</span>
              </label>
              <textarea
                id="blog-text"
                className="input-sales admin-form__text"
                name="text"
                rows={9}
                value={blog.text}
                onChange={handleBlogChange}
                required
              />
              <p className="field__hint">
                Leave a blank line between paragraphs — the story page keeps the
                breaks.
              </p>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="blog-images">
                Photographs
              </label>
              <input
                id="blog-images"
                type="file"
                className="input-sales admin-form__file"
                name="images"
                accept="image/*"
                multiple
                onChange={handleImageChange}
              />
              <p className="field__hint">
                The first photograph becomes the cover image.
              </p>
              {imagePreviews.length ? (
                <ul className="admin-form__previews">
                  {imagePreviews.map((src, index) => (
                    <li key={src}>
                      <img src={src} alt={`Selected photograph ${index + 1}`} />
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            <div aria-live="polite" className="admin-form__status">
              {uploadSuccess ? (
                <p className="notice notice--success">{uploadSuccess}</p>
              ) : null}
              {uploadError ? (
                <p className="notice notice--error">{uploadError}</p>
              ) : null}
            </div>

            <button
              type="submit"
              className="btn-sales btn-sales--block"
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                  Publishing…
                </>
              ) : (
                "Publish story"
              )}
            </button>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminPanel;
